import {
  MobileApp,
  MobileForm,
  MobileListItem,
  MobileScreen,
  MobileSearchAssistant,
  MOBILE_EVENT_CODE,
} from '@mobileTypes'

export const validateMobileApp = (app: Partial<MobileApp>, screens?: MobileScreen[]): string[] => {
  const errors: string[] = []

  if (!app.internalId || app.internalId.trim() === '') {
    errors.push('Internal ID is required')
  }

  if (!app.name || app.name.trim() === '') {
    errors.push('App name is required')
  }

  if (app.startScreen && screens && app.screenIds) {
    // Проверяем, что startScreen находится среди выбранных экранов
    const selectedScreenNames = screens
      .filter(screen => app.screenIds!.includes(screen.id))
      .map(screen => screen.name)
    
    if (!selectedScreenNames.includes(app.startScreen)) {
      errors.push('Start screen must be selected in the Screens tab')
    }
  }

  if (!app.backgroundColor || app.backgroundColor.trim() === '') {
    errors.push('Background color is required')
  }

  // Validate event flows (app-level)
  if (app.eventFlows && app.eventFlows.length > 0) {
    const flowErrors = validateEventFlows(app.eventFlows, [])
    errors.push(...flowErrors)
  }

  return errors
}

export const validateMobileScreen = (screen: Partial<MobileScreen>): string[] => {
  const errors: string[] = []

  if (!screen.internalId || screen.internalId.trim() === '') {
    errors.push('Internal ID is required')
  }

  if (!screen.name || screen.name.trim() === '') {
    errors.push('Screen name is required')
  }

  if (!screen.title || screen.title.trim() === '') {
    errors.push('Screen title is required')
  }

  if (!screen.type) {
    errors.push('Screen type is required')
  }

  if (screen.type === 'LIST' && !screen.listItemConfigId) {
    errors.push('List item configuration is required for LIST type screen')
  }

  if (screen.type === 'FORM' && !screen.formConfigId) {
    errors.push('Form configuration is required for FORM type screen')
  }

  if (screen.type === 'SPLIT') {
    if (!screen.sheetFormConfigId) {
      errors.push('Sheet form configuration is required for SPLIT type screen')
    }
    const bgType = screen.backgroundType || 'LIST'
    if (bgType === 'LIST' && !screen.backgroundListItemConfigId) {
      errors.push('Background list configuration is required when background type is List')
    }
    if (bgType === 'FORM' && !screen.backgroundFormConfigId) {
      errors.push('Background form configuration is required when background type is Form')
    }
    if (
      screen.snapPoints &&
      Array.isArray(screen.snapPoints) &&
      screen.snapPoints.length === 0
    ) {
      errors.push('Snap points must contain at least one value')
    }
  }

  if (screen.type === 'MAP') {
    if (screen.mapConfigId) {
      // Using shared map config — no further check
    } else {
      const center = screen.mapCenter
      if (!center || !Array.isArray(center) || center.length < 2) {
        errors.push('Select a map config in Mobile Maps or set map center (longitude, latitude) for MAP type screen')
      }
    }
  }

  return errors
}

export const validateMobileListItem = (item: Partial<MobileListItem>): string[] => {
  const errors: string[] = []

  if (!item.internalId || item.internalId.trim() === '') {
    errors.push('Internal ID is required')
  }

  if (!item.name || item.name.trim() === '') {
    errors.push('List item name is required')
  }

  const configMode = item.configMode || 'simple'

  if (configMode === 'simple') {
    // Simple mode: validate rows
    if (!item.rows || item.rows.length === 0) {
      errors.push('At least one row is required in Simple mode')
    }
  } else if (configMode === 'advanced') {
    // Advanced mode: validate itemTemplate
    if (!item.itemTemplate || !item.itemTemplate.children || item.itemTemplate.children.length === 0) {
      errors.push('Item template must have at least one element in Advanced mode')
    }
  }

  // Validate eventFlows
  if (item.eventFlows && item.eventFlows.length > 0) {
    const flowErrors = validateEventFlows(item.eventFlows, [])
    errors.push(...flowErrors)
  }

  return errors
}

// Validate event flows for circular dependencies and valid references
const validateEventFlows = (eventFlows: any[], formElements: any[] = []): string[] => {
  const errors: string[] = []

  eventFlows.forEach((flow, flowIndex) => {
    if (!flow.id) {
      errors.push(`Event flow at index ${flowIndex} is missing an ID`)
    }

    if (!flow.eventCode) {
      errors.push(`Event flow "${flow.id || flowIndex}" is missing an event code`)
    }

    // Validate buttonId/fieldId references
    if (flow.buttonId) {
      if (flow.eventCode === MOBILE_EVENT_CODE.ON_BUTTON_SWIPE) {
        // For ON_BUTTON_SWIPE, buttonId must reference a SWIPE element
        const element = formElements.find((el: any) => el.id === flow.buttonId)
        if (!element) {
          errors.push(
            `Event flow "${flow.id || flowIndex}": buttonId "${flow.buttonId}" does not exist in form elements`
          )
        } else if (element.type !== 'SWIPE') {
          errors.push(
            `Event flow "${flow.id || flowIndex}": buttonId "${flow.buttonId}" references element of type "${element.type}", but must be "SWIPE" for ON_BUTTON_SWIPE event`
          )
        }
      } else if (flow.eventCode === MOBILE_EVENT_CODE.ON_BUTTON_PRESS) {
        // For ON_BUTTON_PRESS, buttonId must reference a BUTTON element
        const element = formElements.find((el: any) => el.id === flow.buttonId)
        if (!element) {
          errors.push(
            `Event flow "${flow.id || flowIndex}": buttonId "${flow.buttonId}" does not exist in form elements`
          )
        } else if (element.type !== 'BUTTON') {
          errors.push(
            `Event flow "${flow.id || flowIndex}": buttonId "${flow.buttonId}" references element of type "${element.type}", but must be "BUTTON" for ON_BUTTON_PRESS event`
          )
        }
      }
    }

    if (flow.fieldId) {
      if (flow.eventCode === MOBILE_EVENT_CODE.ON_FIELD_VALUE_CHANGE) {
        // For ON_FIELD_VALUE_CHANGE, fieldId must reference a FIELD element
        const element = formElements.find((el: any) => el.id === flow.fieldId)
        if (!element) {
          errors.push(
            `Event flow "${flow.id || flowIndex}": fieldId "${flow.fieldId}" does not exist in form elements`
          )
        } else if (element.type !== 'FIELD') {
          errors.push(
            `Event flow "${flow.id || flowIndex}": fieldId "${flow.fieldId}" references element of type "${element.type}", but must be "FIELD" for ON_FIELD_VALUE_CHANGE event`
          )
        }
      }
    }

    if (flow.elementId) {
      if (
        flow.eventCode === MOBILE_EVENT_CODE.ON_CHECKBOX_EMBEDDED_LIST_CHANGE ||
        flow.eventCode === MOBILE_EVENT_CODE.ON_EMBEDDED_LIST_ITEM_CONTEXT_MENU
      ) {
        const element = formElements.find((el: any) => el.id === flow.elementId)
        if (!element) {
          errors.push(
            `Event flow "${flow.id || flowIndex}": elementId "${flow.elementId}" does not exist in form elements`
          )
        } else if (element.type !== 'EMBEDDED_LIST') {
          errors.push(
            `Event flow "${flow.id || flowIndex}": elementId "${flow.elementId}" references element of type "${element.type}", but must be "EMBEDDED_LIST" for this event`
          )
        }
      }
    }

    if (!flow.actions || !Array.isArray(flow.actions)) {
      errors.push(`Event flow "${flow.id || flowIndex}" has invalid actions`)

      return
    }

    // Validate actions within the flow
    const actionIds = new Set<string>()
    flow.actions.forEach((action: any, actionIndex: number) => {
      if (!action.id) {
        errors.push(
          `Action at index ${actionIndex} in flow "${flow.id || flowIndex}" is missing an ID`
        )

        return
      }

      // Check for duplicate action IDs within the flow
      if (actionIds.has(action.id)) {
        errors.push(`Duplicate action ID "${action.id}" in flow "${flow.id || flowIndex}"`)
      }
      actionIds.add(action.id)

      if (!action.actionCode) {
        errors.push(`Action "${action.id}" in flow "${flow.id || flowIndex}" is missing an action code`)
      }

      // Validate dependsOn references
      if (action.dependsOn && Array.isArray(action.dependsOn)) {
        action.dependsOn.forEach((depId: string) => {
          if (!actionIds.has(depId) && !flow.actions.some((a: any) => a.id === depId)) {
            errors.push(
              `Action "${action.id}" depends on non-existent action "${depId}" in flow "${flow.id || flowIndex}"`
            )
          }
        })
      }

      // Validate START_INTERVAL action
      if (action.actionCode === 'start_interval') {
        if (!action.meta?.intervalId) {
          errors.push(
            `Action "${action.id}" (START_INTERVAL) is missing required field: intervalId`
          )
        }
        if (!action.meta?.intervalMs || action.meta.intervalMs <= 0) {
          errors.push(
            `Action "${action.id}" (START_INTERVAL) is missing or has invalid intervalMs (must be > 0)`
          )
        }
      }

      // Validate STOP_INTERVAL action
      if (action.actionCode === 'stop_interval') {
        if (!action.meta?.intervalId) {
          errors.push(
            `Action "${action.id}" (STOP_INTERVAL) is missing required field: intervalId`
          )
        }
      }
    })

    // Check for circular dependencies
    const circularErrors = checkCircularDependenciesInFlow(flow.actions, flow.id || flowIndex)
    errors.push(...circularErrors)
  })

  return errors
}

// Check for circular dependencies in action flow
const checkCircularDependenciesInFlow = (actions: any[], flowId: string | number): string[] => {
  const errors: string[] = []
  const visited = new Set<string>()
  const recursionStack = new Set<string>()

  const hasCycle = (actionId: string, path: string[] = []): boolean => {
    if (recursionStack.has(actionId)) {
      errors.push(
        `Circular dependency detected in flow "${flowId}": ${[...path, actionId].join(' -> ')}`
      )

      return true
    }

    if (visited.has(actionId)) {
      return false
    }

    visited.add(actionId)
    recursionStack.add(actionId)

    const action = actions.find(a => a.id === actionId)
    if (action?.dependsOn && Array.isArray(action.dependsOn)) {
      for (const depId of action.dependsOn) {
        if (hasCycle(depId, [...path, actionId])) {
          return true
        }
      }
    }

    recursionStack.delete(actionId)

    return false
  }

  actions.forEach(action => {
    if (action.id && !visited.has(action.id)) {
      hasCycle(action.id)
    }
  })

  return errors
}

export const validateMobileForm = (form: Partial<MobileForm>): string[] => {
  const errors: string[] = []

  if (!form.internalId || form.internalId.trim() === '') {
    errors.push('Internal ID is required')
  }

  if (!form.name || form.name.trim() === '') {
    errors.push('Form name is required')
  }

  if (!form.elements || form.elements.length === 0) {
    errors.push('Form must have at least one element')
  }

  // Validate EMBEDDED_LIST and LIST_ITEM elements
  const allElements = [...(form.elements || []), ...(form.bottomElements || [])]
  allElements.forEach((element, index) => {
    if (element.type === 'EMBEDDED_LIST') {
      if (!element.embeddedListItemId && !element.embeddedListConfig) {
        errors.push(`Element ${index + 1} (EMBEDDED_LIST): Either embeddedListItemId or embeddedListConfig must be specified`)
      }
      if (element.selectedIdsVariable && !form.variables?.find(v => v.internalId === element.selectedIdsVariable)) {
        errors.push(`Element ${index + 1} (EMBEDDED_LIST): Variable "${element.selectedIdsVariable}" not found`)
      }
    }
    if (element.type === 'LIST_ITEM') {
      if (!element.listItemConfigId) {
        errors.push(`Element ${index + 1} (LIST_ITEM): listItemConfigId must be specified`)
      }
      if (element.listItemDataVariable && !form.variables?.find(v => v.internalId === element.listItemDataVariable)) {
        errors.push(`Element ${index + 1} (LIST_ITEM): Variable "${element.listItemDataVariable}" not found`)
      }
    }
  })

  // Validate event flows
  if (form.eventFlows && form.eventFlows.length > 0) {
    const flowErrors = validateEventFlows(form.eventFlows, allElements)
    errors.push(...flowErrors)
  }

  return errors
}

export const validateMobileSearchAssistant = (
  searchAssistant: Partial<MobileSearchAssistant>
): string[] => {
  const errors: string[] = []

  if (!searchAssistant.name || searchAssistant.name.trim() === '') {
    errors.push('Search Assistant name is required')
  }

  if (!searchAssistant.title || searchAssistant.title.trim() === '') {
    errors.push('Search Assistant title is required')
  }

  // Check for unique filter IDs
  if (searchAssistant.filters && searchAssistant.filters.length > 0) {
    const filterIds = searchAssistant.filters.map(f => f.id)
    const uniqueIds = new Set(filterIds)
    if (filterIds.length !== uniqueIds.size) {
      errors.push('Filter IDs must be unique')
    }

    // Validate each filter
    searchAssistant.filters.forEach((filter, index) => {
      if (!filter.label || filter.label.trim() === '') {
        errors.push(`Filter ${index + 1}: Label is required`)
      }

      if (!filter.fieldCode || filter.fieldCode.trim() === '') {
        errors.push(`Filter ${index + 1}: Field code is required`)
      }

      if (!filter.operator) {
        errors.push(`Filter ${index + 1}: Operator is required`)
      }
    })
  }

  // Validate eventFlows
  if (searchAssistant.eventFlows && searchAssistant.eventFlows.length > 0) {
    const flowErrors = validateEventFlows(searchAssistant.eventFlows, [])
    errors.push(...flowErrors)
  }

  return errors
}

export const isValidJSON = (str: string): boolean => {
  try {
    JSON.parse(str)

    return true
  } catch {
    return false
  }
}
